import { useEffect, useMemo, useRef, useState } from "react";
import "./CriarDesvio.css";

const UP = (v) => (typeof v === "string" ? v.toUpperCase() : v ?? "");
const ATIVO_OPTIONS = [
  { value: 1, label: "SIM" },
  { value: 0, label: "NÃO" },
];

function CriarDesvioDropdown({ label, name, value, options, onSelect, placeholder = "SELECIONE" }) {
  const [open, setOpen] = useState(false);
  const [renderMenu, setRenderMenu] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(220);
  const wrapRef = useRef(null);
  const closeTimerRef = useRef(null);
  const itemHeightRef = 44;

  const current = useMemo(() => options.find((o) => o.value === value) || null, [options, value]);

  useEffect(() => {
    function onDocDown(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) closeMenu();
    }
    document.addEventListener("mousedown", onDocDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const onWindowChange = () => {
      updatePlacement();
    };

    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);
    return () => {
      window.removeEventListener("resize", onWindowChange);
      window.removeEventListener("scroll", onWindowChange, true);
    };
  }, [open, options.length]);

  function updatePlacement() {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const modal = wrapRef.current.closest(".dvcr-box");
    const modalRect = modal?.getBoundingClientRect();

    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const safePad = 12;

    const viewportBelow = Math.max(0, viewportH - rect.bottom - safePad);
    const viewportAbove = Math.max(0, rect.top - safePad);
    const modalBelow = modalRect ? Math.max(0, modalRect.bottom - rect.bottom - safePad) : Number.POSITIVE_INFINITY;
    const modalAbove = modalRect ? Math.max(0, rect.top - modalRect.top - safePad) : Number.POSITIVE_INFINITY;

    const spaceBelow = Math.max(0, Math.min(viewportBelow, modalBelow));
    const spaceAbove = Math.max(0, Math.min(viewportAbove, modalAbove));

    const target = Math.min(320, options.length * itemHeightRef + 12);
    const shouldOpenUp = spaceBelow < 190 && spaceAbove > spaceBelow;
    const available = shouldOpenUp ? spaceAbove : spaceBelow;
    const dynamicMax = Math.max(96, Math.min(target, available - 6));

    setOpenUp(shouldOpenUp);
    setMenuMaxHeight(dynamicMax);
  }

  const openMenu = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setRenderMenu(true);
    requestAnimationFrame(() => {
      updatePlacement();
      setOpen(true);
    });
  };

  const closeMenu = () => {
    setOpen(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setRenderMenu(false), 240);
  };

  const toggle = () => {
    if (open) closeMenu();
    else openMenu();
  };

  return (
    <div ref={wrapRef} className={`dvcr-field dvcr-field-select ${open ? "is-open" : ""} ${openUp ? "is-open-up" : ""}`}>
      <label className="dvcr-label">{label}</label>
      <div className="dvcr-pick-anchor">
        <button
          type="button"
          className="dvcr-pick-btn"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={toggle}
        >
          <span className={`dvcr-pick-val ${current ? "" : "is-placeholder"}`}>
            {current ? current.label : placeholder}
          </span>
        </button>

        {renderMenu && (
          <div
            className={`dvcr-pick-menu ${open ? "is-open" : "is-closing"}`}
            role="listbox"
            aria-label={label}
            style={{ maxHeight: `${menuMaxHeight}px` }}
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="dvcr-pick-opt"
                onClick={() => {
                  onSelect(name, opt.value);
                  closeMenu();
                }}
                role="option"
                aria-selected={opt.value === value}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CriarDesvio({ open, contexto, onClose, onSalvar, onConfirm }) {
  const submit = onSalvar || onConfirm || (() => {});

  const [form, setForm] = useState({
    causador: contexto?.causador || "",
    causa_primaria: contexto?.causa_primaria || "",
    causa_secundaria: "",
    quando_utilizado: "",
    ativo: "",
  });

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setForm({
        causador: contexto?.causador || "",
        causa_primaria: contexto?.causa_primaria || "",
        causa_secundaria: "",
        quando_utilizado: "",
        ativo: "",
      });
    }
  }, [open, contexto]);

  if (!open) return null;

  return (
    <div className="dvcr-bg">
      <div className="dvcr-box" role="dialog" aria-modal="true" aria-label="Criar desvio">
        <header className="dvcr-head">
          <h3 className="dvcr-title">Cadastrar desvio</h3>
          <button className="dvcr-x" type="button" onClick={onClose} aria-label="Fechar">x</button>
        </header>

        <div className="dvcr-body">
          <div className="dvcr-row">
            <div className="dvcr-field">
              <label className="dvcr-label">Causador</label>
              <input
                className="dvcr-input"
                value={form.causador}
                onChange={(e) => setForm((s) => ({ ...s, causador: UP(e.target.value) }))}
                placeholder="EX.: INFRAESTRUTURA"
              />
            </div>
            <div className="dvcr-field">
              <label className="dvcr-label">Causa Primaria</label>
              <input
                className="dvcr-input"
                value={form.causa_primaria}
                onChange={(e) => setForm((s) => ({ ...s, causa_primaria: UP(e.target.value) }))}
                placeholder="EX.: MATERIAL"
              />
            </div>
          </div>

          <div className="dvcr-row">
            <div className="dvcr-field">
              <label className="dvcr-label">Causa Secundaria</label>
              <input
                className="dvcr-input"
                value={form.causa_secundaria}
                onChange={(e) => setForm((s) => ({ ...s, causa_secundaria: UP(e.target.value) }))}
                placeholder="EX.: FALHA DE COMPONENTE"
              />
            </div>

            <CriarDesvioDropdown
              label="Ativo"
              name="ativo"
              value={form.ativo}
              options={ATIVO_OPTIONS}
              onSelect={(name, nextValue) => setForm((s) => ({ ...s, [name]: Number(nextValue) }))}
            />
          </div>

          <div className="dvcr-row">
            <div className="dvcr-field dvcr-grow">
              <label className="dvcr-label">Quando Utilizado</label>
              <textarea
                className="dvcr-input"
                rows={3}
                value={form.quando_utilizado}
                onChange={(e) => setForm((s) => ({ ...s, quando_utilizado: e.target.value }))}
                placeholder="Descreva quando utilizar..."
              />
            </div>
          </div>
        </div>

        <footer className="dvcr-foot">
          <button className="dvcr-btn" type="button" onClick={onClose}>Cancelar</button>
          <button
            className="dvcr-btn dvcr-btn-ok"
            type="button"
            onClick={() => submit(form)}
            disabled={
              !form.causador ||
              !form.causa_primaria ||
              !form.causa_secundaria ||
              !form.quando_utilizado ||
              (form.ativo !== 0 && form.ativo !== 1)
            }
          >
            Cadastrar
          </button>
        </footer>
      </div>
    </div>
  );
}
